import React, { createContext, useContext, ReactNode } from 'react';
import { useToast } from '../hooks/useToast';
import Toast from '../components/common/Toast';

interface ToastContextType {
  showToast: (message: string, type?: 'info' | 'warning' | 'error' | 'success', duration?: number) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToastContext = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const { toasts, showToast, removeToast, showInfo, showWarning, showError, showSuccess } = useToast();

  const contextValue = {
    showToast,
    showInfo,
    showWarning,
    showError,
    showSuccess
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Render toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};